import React, { useState, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { bulkCreateProducts } from '../services/api';
import toast from 'react-hot-toast';

const ExcelUploadModal = ({ show, onHide, onSuccess }) => {
    const { t, i18n } = useTranslation();
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, []);

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file) => {
        // Simple validation for Excel extensions
        if (!file.name.match(/\.(xlsx|xls)$/)) {
            toast.error(t('productsPage.bulkUpload.dropzoneHint'));
            return;
        }
        setSelectedFile(file);
        setError(null);
        setValidationErrors([]);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError(null);
        setValidationErrors([]);

        const toastId = toast.loading(t('productsPage.bulkUpload.uploading'));

        try {
            const response = await bulkCreateProducts(selectedFile);

            if (response.data.errors && response.data.errors.length > 0) {
                // Partial success logic if needed, but for now we display errors if any
                // If success but with warnings/errors
            }

            toast.success(t('productsPage.bulkUpload.successMessage', { count: response.data.count }), { id: toastId });

            if (onSuccess) onSuccess();
            handleClose();
        } catch (err) {
            console.error(err);

            // Handle translated error message
            let errorMsg = t('productsPage.bulkUpload.errorMessage');
            if (err.response?.data?.messageCode) {
                // Pasa el resto del payload como variables de interpolación
                // (p. ej. { max, current, attempted } de ROW_CAP_EXCEEDED / PLAN_LIMIT_EXCEEDED).
                errorMsg = t(`productsPage.bulkUpload.errors.${err.response.data.messageCode}`, err.response.data);
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }

            // Format validation errors with translations
            let valErrors = [];
            if (err.response?.data?.errors) {
                valErrors = err.response.data.errors.map(error => {
                    if (typeof error === 'object' && error.code) {
                        // Translate field names
                        const translatedFields = error.fields
                            ? error.fields.map(f => t(`productsPage.bulkUpload.fields.${f}`)).join(', ')
                            : '';

                        return t(`productsPage.bulkUpload.errors.${error.code}`, {
                            row: error.row,
                            fields: translatedFields
                        });
                    }
                    return error; // Fallback for string errors
                });
            }

            setError(errorMsg);
            setValidationErrors(valErrors);
            toast.error(errorMsg, { id: toastId });
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setError(null);
        setValidationErrors([]);
        onHide();
    };

    const getTemplateUrl = () => {
        // Detect language and return appropriate template
        if (i18n.language === 'es') {
            return '/assets/plantilla_productos.xlsx';
        }
        return '/assets/products_template.xlsx';
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('productsPage.bulkUpload.modalTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <p className="mb-2 text-muted">{t('productsPage.bulkUpload.downloadTemplate')}:</p>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        href={getTemplateUrl()}
                        download
                        target="_blank"
                    >
                        <i className="bi bi-download me-2"></i>
                        {t('productsPage.bulkUpload.downloadTemplate')}
                    </Button>
                </div>

                <div
                    className={`p-5 mb-3 text-center border rounded ${dragActive ? 'bg-light border-primary' : 'border-secondary'}`}
                    style={{ borderStyle: 'dashed', cursor: 'pointer', transition: 'all 0.2s' }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-upload').click()}
                >
                    <input
                        type="file"
                        id="file-upload"
                        className="d-none"
                        accept=".xlsx, .xls"
                        onChange={handleChange}
                    />

                    {selectedFile ? (
                        <div>
                            <div className="fs-1 text-success mb-2">
                                <i className="bi bi-file-earmark-excel-fill"></i>
                            </div>
                            <p className="mb-0 fw-bold">{selectedFile.name}</p>
                            <p className="text-muted small">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                            <Button variant="link" className="text-danger p-0 mt-2" onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile(null);
                            }}>
                                {t('common.remove', 'Eliminar')}
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <div className="fs-1 text-muted mb-2">
                                <i className="bi bi-cloud-upload"></i>
                            </div>
                            <p className="mb-2">{t('productsPage.bulkUpload.dropzoneText')}</p>
                            <p className="text-muted small mb-0">{t('productsPage.bulkUpload.dropzoneHint')}</p>
                        </div>
                    )}
                </div>

                {error && (
                    <Alert variant="danger">
                        {error}
                        {validationErrors.length > 0 && (
                            <ul className="mb-0 mt-2 small">
                                {validationErrors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                ))}
                            </ul>
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={uploading}>
                    {t('common.close', 'Cerrar')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            {t('productsPage.bulkUpload.uploading')}
                        </>
                    ) : (
                        t('productsPage.bulkUpload.uploadButton')
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExcelUploadModal;
